import { HyroLoader } from "@hyro-components";
import React, { useEffect, useMemo, useState } from "react";
import {
  accountManagementErrorSelector,
  accountManagementErrorTextSelector,
  createAccountSelector,
  deleteAccountSelector,
  editAccountSelector,
  getAccountsAdminSelector,
  hasPermissionSelector,
  managementAccountsSelector,
  setAccountManagementErrorSelector
} from "src/stores/selectors/appSelectors";
import AccountEditorDialog from "./AccountEditorDialog";
import AccountEditorDrawer from "./AccountEditorDrawer";
import AddIcon from "@mui/icons-material/Add";
import { DEFAULT_TIMEZONE, TIMEZONE_OPTIONS } from "@consts";
import { DIALOG_TYPES } from "./AccountEditorConsts";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "../../../stores/appStore";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";
import dayjs from "dayjs";
import { Button } from "@mui/material";
import { COLUMNS, FILTERS } from "./AccountManagement.consts";
import HyroTableContainer from "@hyro-components/hyro-table-container/HyroTableContainer";

const initialDialogState = {
  open: false,
  type: "",
  error: false,
  errorText: "",
  accountData: { accountId: { value: "", errorText: "" }, name: { value: "", errorText: "" }, timezone: { value: DEFAULT_TIMEZONE, errorText: "" } }
};

const AccountManagement = () => {
  const getAccounts = useAppStore(getAccountsAdminSelector);
  const createAccount = useAppStore(createAccountSelector);
  const editAccount = useAppStore(editAccountSelector);
  const deleteAccount = useAppStore(deleteAccountSelector);
  const error = useAppStore(accountManagementErrorSelector);
  const errorText = useAppStore(accountManagementErrorTextSelector);
  const setError = useAppStore(setAccountManagementErrorSelector);
  const rawAccounts = useAppStore(managementAccountsSelector);
  const dashboardSettings = useFetchDashboardSettings()?.data;
  const orderedAccounts = useMemo(
    () => rawAccounts?.sort((a, b) => a.name.localeCompare(b.name)),
    [rawAccounts]
  );
  const hasPermission = useAppStore(hasPermissionSelector);
  const createAccountPermission = hasPermission(
    USER_PERMISSIONS.ACCOUNTS.WRITE
  );
  const [accountEditorDialog, setAccountEditorDialog]
    = useState(initialDialogState);

  useEffect(() => {
    if (error) {
      setAccountEditorDialog({ ...accountEditorDialog, error, errorText });
    }

  }, [error, errorText, setError]);

  useEffect(() => {
    if (!error) {
      setAccountEditorDialog(initialDialogState);
    }

  }, [rawAccounts]);

  useEffect(() => {
    getAccounts();
  }, [getAccounts]);

  const handleAccountEditorDialogClose = () => {
    setAccountEditorDialog(initialDialogState);
    setError(null);
  };

  const handleAccountEditorConfirmation = (data) => {
    if (accountEditorDialog.type === DIALOG_TYPES.create) {
      createAccount({
        name: data.name.value,
        timezone: data.timezone.value
      });
    }
    else if (accountEditorDialog.type === DIALOG_TYPES.edit) {
      editAccount({
        ...data,
        name: data.name.value,
        accountId: data.accountId.value,
        timezone: data.timezone.value
      });
    }
    else if (accountEditorDialog.type === DIALOG_TYPES.delete) {
      deleteAccount({
        ...data,
        name: data.name.value,
        accountId: data.accountId.value
      });
    }
  };

  const handleResetError = () => {
    setAccountEditorDialog({ ...accountEditorDialog, error: false });
    setError(null);
  };

  const cols = [
    { field: COLUMNS.name.id, headerName: COLUMNS.name.label },
    { field: COLUMNS.accountId.id, headerName: COLUMNS.accountId.label },
    { field: COLUMNS.creator.id, headerName: COLUMNS.creator.label },
    { field: COLUMNS.createdAt.id, headerName: COLUMNS.createdAt.label, renderCell: ({ row }) => dayjs(new Date(row.createdAt)).format("MM/DD/YYYY HH:ss a") },
    { field: COLUMNS.timezone.id, headerName: COLUMNS.timezone.label, width: 250, renderCell: ({ row }) => {
      const timezoneKey = row.timezone || DEFAULT_TIMEZONE;
      const timezoneOption = TIMEZONE_OPTIONS.find(option => option.key === timezoneKey);
      return timezoneOption?.display_key || timezoneKey;
    } },
    { field: COLUMNS.twilioId.id, headerName: COLUMNS.twilioId.label }
  ];

  if (!rawAccounts) return <HyroLoader />;

  const createNewAccountButton = createAccountPermission && (
    <Button
      data-testid="account-management-create-new-account-button"
      startIcon={<AddIcon />}
      disabled={dashboardSettings?.disableChanges}
      color="primary"
      size="medium"
      variant="contained"
      onClick={() => {
        setAccountEditorDialog({
          ...accountEditorDialog,
          open: true,
          type: DIALOG_TYPES.create
        });
      }}
    >
      Create New Account
    </Button>
  );

  return (
    <>
      <HyroTableContainer
        data={orderedAccounts || []}
        header={{
          title: "Accounts",
          showDownloadButton: false,
          children: createNewAccountButton
        }}
        filters={{
          initialFilters: FILTERS
        }}
        columns={cols}
        onRowClick={({ row }) => {
          window.open(
            `${window.location.protocol}//${window.location.host}/${row.accountId}`
          );
        }}
        rowActions={
          createAccountPermission && !dashboardSettings?.disableChanges
            ? [
              {
                id: "edit",
                display: "Settings",
                run: (accountData) => {
                  setAccountEditorDialog({
                    open: true,
                    type: DIALOG_TYPES.edit,
                    accountData: {
                      ...accountData,
                      name: { value: accountData.name, errorText: "" },
                      accountId: {
                        value: accountData.accountId,
                        errorText: ""
                      },
                      timezone: {
                        value: accountData.timezone || DEFAULT_TIMEZONE,
                        errorText: ""
                      }
                    }
                  });
                }
              },
              {
                id: "delete",
                display: "Delete account",
                run: (accountData) => {
                  setAccountEditorDialog({
                    open: true,
                    type: DIALOG_TYPES.delete,
                    accountData: {
                      ...accountData,
                      name: { value: accountData.name, errorText: "" },
                      accountId: {
                        value: accountData.accountId,
                        errorText: ""
                      }
                    }
                  });
                }
              }
            ]
            : null
        }
        testId="account-management"
      />
      {accountEditorDialog.open && accountEditorDialog.type === DIALOG_TYPES.edit && (
        <AccountEditorDrawer
          {...accountEditorDialog}
          onResetError={handleResetError}
          onSubmit={handleAccountEditorConfirmation}
          onClose={handleAccountEditorDialogClose}
        />
      )}
      {accountEditorDialog.open && accountEditorDialog.type !== DIALOG_TYPES.edit && (
        <AccountEditorDialog
          {...accountEditorDialog}
          onResetError={handleResetError}
          onConfirm={handleAccountEditorConfirmation}
          onClose={handleAccountEditorDialogClose}
        />
      )}
    </>
  );
};

export default AccountManagement;
